import React, { forwardRef, useEffect, useState } from 'react';
import { MDBBtn, MDBDatatable, MDBIcon } from 'mdb-react-ui-kit';
import DatePicker from 'react-datepicker';
import calImg from '../components/images/calendar.svg';
import ExcelExportButton from '../components/exportToExcel.js';
import Loader from '../components/Loader.js';
import { getPinelabsSettlementReport, syncPinelabsSettlement, savePinelabsSettlement } from '../services/apiServices.js';
import { format } from 'date-fns';

const DateInput = forwardRef(({ value, onClick }, ref) => (
  <div
    className="d-flex align-items-center border rounded px-2 py-1"
    style={{ cursor: 'pointer', minWidth: 130 }}
    onClick={onClick}
    ref={ref}
  >
    <img src={calImg} alt="cal" style={{ width: 16, marginRight: 6 }} />
    <span style={{ fontSize: 13 }}>{value || 'Select date'}</span>
  </div>
));

const STATUS_STYLE = {
  success  : { background: '#e8f5e9', color: '#2e7d32', label: 'Success'   },
  pending  : { background: '#fff8e1', color: '#f57f17', label: 'Pending'   },
  failed   : { background: '#fce4ec', color: '#c62828', label: 'Failed'    },
  cancelled: { background: '#f3e5f5', color: '#6a1b9a', label: 'Cancelled' },
  reversed : { background: '#fff3e0', color: '#e65100', label: 'Reversed'  },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || { background: '#f5f5f5', color: '#555', label: status };
  return (
    <span style={{
      background: s.background, color: s.color,
      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    }}>
      {s.label}
    </span>
  );
};

const fmtRs = (v) => v == null ? '—' : `₹${Number(v).toFixed(2)}`;

function EditSettlementModal({ row, onClose, onSaved }) {
  const [mdr,      setMdr]      = useState(row.tdrAmount      != null ? row.tdrAmount      : '');
  const [tax,      setTax]      = useState(row.taxOnTdr       != null ? row.taxOnTdr       : '');
  const [released, setReleased] = useState(row.releasedAmount != null ? row.releasedAmount : '');
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState('');

  async function handleSave() {
    setSaving(true); setErr('');
    try {
      const res  = await savePinelabsSettlement(row.ptrid, {
        tdrAmount     : mdr      !== '' ? parseFloat(mdr)      : null,
        taxOnTdr      : tax      !== '' ? parseFloat(tax)      : null,
        releasedAmount: released !== '' ? parseFloat(released) : null,
      });
      if (!res || res === 'false') { setErr('Save failed'); setSaving(false); return; }
      const data = await res.json();
      if (data.success) { onSaved(data); onClose(); }
      else setErr(data.error || 'Save failed');
    } catch (e) { setErr(e.message); }
    setSaving(false);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: 28, width: 360,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <h6 className="fw-bold mb-1">Enter Settlement Amounts</h6>
        <p className="text-muted mb-3" style={{ fontSize: 12 }}>
          PTRID: <strong>{row.ptrid}</strong> &nbsp;|&nbsp; Gross: <strong>{fmtRs(row.grossAmount)}</strong>
        </p>
        <div className="mb-2">
          <label className="form-label small mb-1">MDR / TDR Amount (₹)</label>
          <input className="form-control form-control-sm" type="number" step="0.01"
            placeholder="e.g. 18.00"
            value={mdr} onChange={e => setMdr(e.target.value)} />
        </div>
        <div className="mb-2">
          <label className="form-label small mb-1">Tax on MDR (₹)</label>
          <input className="form-control form-control-sm" type="number" step="0.01"
            placeholder="e.g. 3.24"
            value={tax} onChange={e => setTax(e.target.value)} />
        </div>
        <div className="mb-3">
          <label className="form-label small mb-1">Released / Settled Amount (₹)</label>
          <input className="form-control form-control-sm" type="number" step="0.01"
            placeholder="e.g. 978.76"
            value={released} onChange={e => setReleased(e.target.value)} />
        </div>
        {err && <div className="text-danger small mb-2">{err}</div>}
        <div className="d-flex gap-2">
          <MDBBtn size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </MDBBtn>
          <MDBBtn size="sm" color="secondary" onClick={onClose} disabled={saving}>Cancel</MDBBtn>
        </div>
      </div>
    </div>
  );
}

function PinelabsSettlementReport() {
  const gymId = localStorage.getItem('loggedUserInfo')
    ? JSON.parse(localStorage.getItem('loggedUserInfo')).gymid : '';

  const startOfThisMonth = () => {
    const d = new Date();
    d.setDate(1);
    return d;
  };

  const [datePreset, setDatePreset] = useState('month'); // 'today' | 'yesterday' | 'month' | 'custom'
  const [fromDate,   setFromDate]   = useState(startOfThisMonth());
  const [toDate,     setToDate]     = useState(new Date());
  const [loading,    setLoading]    = useState(false);
  const [rows,       setRows]       = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [tdrAvail,   setTdrAvail]   = useState(false);
  const [tableData,  setTableData]  = useState({ columns: [], rows: [] });
  const [syncing,    setSyncing]    = useState(false);
  const [syncMsg,    setSyncMsg]    = useState(null);
  const [editRow,    setEditRow]    = useState(null); // row being edited

  async function fetchReport(overrideFrom, overrideTo) {
    if (!gymId) return;
    const f = overrideFrom || fromDate;
    const t = overrideTo   || toDate;
    setLoading(true);
    try {
      const res  = await getPinelabsSettlementReport(
        gymId,
        format(f, 'yyyy-MM-dd'),
        format(t, 'yyyy-MM-dd'),
      );
      if (!res || res === 'false') { setLoading(false); return; }
      const data = await res.json();
      setRows(data.transactions || []);
      setSummary(data.summary   || null);
      setTdrAvail((data.transactions || []).some(r => r.tdrAmount != null));
    } catch (e) {
      console.error('Settlement report error:', e);
    }
    setLoading(false);
  }

  function getPresetRange(preset) {
    const now = new Date();
    if (preset === 'today') {
      return [now, now];
    }
    if (preset === 'yesterday') {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      return [y, y];
    }
    if (preset === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return [start, now];
    }
    return null; // custom — leave fromDate/toDate as whatever the user picks
  }

  function handlePresetChange(preset) {
    setDatePreset(preset);
    if (preset === 'custom') return;
    const [f, t] = getPresetRange(preset);
    setFromDate(f);
    setToDate(t);
    fetchReport(f, t);
  }

  useEffect(() => { fetchReport(); }, []);

  useEffect(() => {
    const baseCols = [
      { label: 'Date',          field: 'date' },
      { label: 'Status',        field: 'statusBadge' },
      { label: 'PTRID',         field: 'ptrid' },
      { label: 'Token (tId)',   field: 'tId' },
      { label: 'Trans ID',      field: 'orderId' },
      { label: 'Member',        field: 'memberName' },
      { label: 'Plan',          field: 'planName' },
      { label: 'Mode',          field: 'paymentMode' },
      { label: 'Amount',         field: 'grossAmount' },
      ...(tdrAvail ? [
        { label: 'MDR (TDR)',   field: 'tdrAmount' },
        { label: 'Tax on MDR',  field: 'taxOnTdr' },
        { label: 'Released Amt',field: 'releasedAmount' },
      ] : []),
      { label: 'Device ID',      field: 'deviceNumber' },
      { label: 'Device Name',   field: 'deviceName' },
      { label: 'Approval Code', field: 'approvalCode' },
      { label: 'Trans Log ID',  field: 'rrn' },
      { label: 'Bank',          field: 'acquirerName' },
      { label: 'Failure Reason',field: 'failureReason' },
      { label: '',              field: 'editBtn' },
    ];

    const tableRows = rows.map(r => ({
      date          : r.date ? new Date(r.date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—',
      statusBadge   : <StatusBadge status={r.status} />,
      ptrid         : r.ptrid         || '—',
      tId           : r.tId           || '—',
      orderId       : r.orderId       || '—',
      memberName    : r.memberName    || '—',
      planName      : r.planName      || '—',
      paymentMode   : r.paymentMode   || '—',
      grossAmount   : fmtRs(r.grossAmount),
      tdrAmount     : fmtRs(r.tdrAmount),
      taxOnTdr      : fmtRs(r.taxOnTdr),
      releasedAmount: fmtRs(r.releasedAmount),
      deviceNumber  : r.deviceNumber  || '—',
      deviceName    : r.deviceName    || '—',
      approvalCode  : r.approvalCode  || '—',
      rrn           : r.rrn           || '—',
      acquirerName  : r.acquirerName  || '—',
      failureReason : r.failureReason || '—',
      editBtn       : r.status === 'success'
        ? (
          <button
            className="btn btn-sm btn-outline-primary py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => setEditRow(r)}
            title="Enter MDR / Released Amount"
          >
            <MDBIcon fas icon="pencil-alt" className="me-1" />Edit
          </button>
        ) : null,
    }));

    setTableData({ columns: baseCols, rows: tableRows });
  }, [rows, tdrAvail]);

  async function handleSync() {
    if (!gymId) return;
    setSyncing(true); setSyncMsg(null);
    try {
      const res  = await syncPinelabsSettlement(gymId, format(fromDate, 'yyyy-MM-dd'), format(toDate, 'yyyy-MM-dd'));
      if (!res || res === 'false') { setSyncing(false); return; }
      const data = await res.json();
      setSyncMsg(`Synced ${data.updated ?? 0} transaction(s).`);
      await fetchReport();
    } catch (e) { setSyncMsg('Sync failed: ' + e.message); }
    setSyncing(false);
  }

  function handleExport() {
    const columns = [
      'S.No', 'Date & Time', 'Status', 'PTRID', 'Token (tId)', 'Trans ID', 'Member', 'Plan',
      'Mode', 'Amount', 'MDR (TDR)', 'Tax on MDR', 'Released Amt',
      'Device ID', 'Device Name', 'Approval Code', 'Trans Log ID', 'Bank', 'Failure Reason',
    ];
    const exportRows = rows.map((r, i) => [
      i + 1,
      r.date ? new Date(r.date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '',
      r.status || '',
      r.ptrid         || '',
      r.tId           || '',
      r.orderId       || '',
      r.memberName    || '',
      r.planName      || '',
      r.paymentMode   || '',
      r.grossAmount    != null ? r.grossAmount    : '',
      r.tdrAmount      != null ? r.tdrAmount      : '',
      r.taxOnTdr       != null ? r.taxOnTdr       : '',
      r.releasedAmount != null ? r.releasedAmount : '',
      r.deviceNumber      || '',
      r.deviceName        || '',
      r.approvalCode       || '',
      r.transactionLogId || r.rrn || '',
      r.acquirerName      || '',
      r.failureReason      || '',
    ]);
    ExcelExportButton({ columns, rows: exportRows }, 'PineLabs_Settlement_Report');
  }

  return (
    <div>
      {loading && <Loader />}

      {editRow && (
        <EditSettlementModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); fetchReport(); }}
        />
      )}

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: 13 }}>Period</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 140 }}
            value={datePreset}
            onChange={(e) => handlePresetChange(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="month">This Month</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {datePreset === 'custom' && <>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: 13 }}>From</span>
            <DatePicker selected={fromDate} onChange={setFromDate} maxDate={toDate}
              dateFormat="dd MMM yyyy" customInput={<DateInput />} />
          </div>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: 13 }}>To</span>
            <DatePicker selected={toDate} onChange={setToDate} minDate={fromDate}
              maxDate={new Date()} dateFormat="dd MMM yyyy" customInput={<DateInput />} />
          </div>
          <MDBBtn size="sm" onClick={() => fetchReport()} disabled={loading}>
            <MDBIcon fas icon="search" className="me-1" /> Search
          </MDBBtn>
        </>}

        <MDBBtn size="sm" color="warning" onClick={handleSync} disabled={syncing || loading}>
          <MDBIcon fas icon="sync" className="me-1" /> {syncing ? 'Syncing…' : 'Sync Settlement'}
        </MDBBtn>
        {rows.length > 0 && (
          <MDBBtn size="sm" color="success" onClick={handleExport}>
            <MDBIcon fas icon="file-excel" className="me-1" /> Export
          </MDBBtn>
        )}
      </div>
      {syncMsg && <div className="alert alert-info py-2 mb-3" style={{ fontSize: 12 }}>{syncMsg}</div>}

      {/* ── Summary Cards ──────────────────────────────────────────────── */}
      {summary && (
        <div className="d-flex flex-wrap gap-3 mb-3">
          <SummaryCard label="Total"     value={summary.total}     />
          <SummaryCard label="Success"   value={summary.success}   color="#2e7d32" bg="#e8f5e9" />
          <SummaryCard label="Pending"   value={summary.pending}   color="#f57f17" bg="#fff8e1" />
          <SummaryCard label="Failed"    value={summary.failed}    color="#c62828" bg="#fce4ec" />
          <SummaryCard label="Cancelled" value={summary.cancelled} color="#6a1b9a" bg="#f3e5f5" />
        </div>
      )}

      {summary && summary.success > 0 && (
        <div className="d-flex flex-wrap gap-3 mb-4">
          <SummaryCard label="Amount (Success)"  value={`₹${summary.grossAmount?.toFixed(2)}`}   />
          {tdrAvail && <>
            <SummaryCard label="MDR (TDR)"      value={`₹${summary.totalTdr?.toFixed(2)}`}      color="#e65100" />
            <SummaryCard label="Tax on MDR"     value={`₹${summary.totalTaxOnTdr?.toFixed(2)}`} color="#e65100" />
            <SummaryCard label="Total Released" value={`₹${summary.totalReleased?.toFixed(2)}`} color="#1565c0" bg="#e3f2fd" />
          </>}
        </div>
      )}

      {!tdrAvail && rows.some(r => r.status === 'success') && (
        <div className="alert alert-info py-2 mb-3" style={{ fontSize: 12 }}>
          MDR / Released amount not yet available from Pine Labs. Click <strong>✏️ Edit</strong> on a row to enter values manually from the Pine Labs merchant portal.
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      {rows.length > 0
        ? <MDBDatatable data={tableData} entries={25} fixedHeader style={{ fontSize: 13 }} />
        : !loading && (
          <div className="text-center text-muted py-5">
            No Pine Labs transactions found for the selected period.
          </div>
        )
      }
    </div>
  );
}

function SummaryCard({ label, value, color, bg }) {
  return (
    <div style={{
      background: bg || '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
      padding: '10px 18px', minWidth: 130, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || '#1a1a2e' }}>{value}</div>
    </div>
  );
}

export default PinelabsSettlementReport;
